import React, { useEffect, useRef, useState } from 'react';
import PencilIcon from './icons/PencilIcon';

interface EditableMealTitleProps {
    value: string;
    canEdit: boolean;
    onSave: (nextTitle: string | null) => void;
    className?: string;
    style?: React.CSSProperties;
    inputClassName?: string;
    placeholder?: string;
    editLabel?: string;
}

const EditableMealTitle: React.FC<EditableMealTitleProps> = ({
    value,
    canEdit,
    onSave,
    className = '',
    style,
    inputClassName = '',
    placeholder = '',
    editLabel = 'Edit meal name',
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!isEditing) {
            setDraft(value);
        }
    }, [value, isEditing]);

    useEffect(() => {
        if (isEditing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [isEditing]);

    const commit = () => {
        const trimmed = draft.trim();
        onSave(trimmed.length > 0 ? trimmed : null);
        setIsEditing(false);
    };

    const cancel = () => {
        setDraft(value);
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <input
                ref={inputRef}
                type="text"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commit}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        commit();
                    }
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        cancel();
                    }
                }}
                placeholder={placeholder}
                className={inputClassName}
                style={style}
                aria-label={editLabel}
            />
        );
    }

    return (
        <span className={`inline-flex items-center gap-1 min-w-0 ${className}`}>
            <span className="truncate">{value}</span>
            {canEdit && (
                <button
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation();
                        setIsEditing(true);
                    }}
                    className="mealpai-no-print shrink-0 opacity-60 hover:opacity-100 transition-opacity p-0.5 rounded"
                    aria-label={editLabel}
                    title={editLabel}
                >
                    <PencilIcon className="w-3 h-3" />
                </button>
            )}
        </span>
    );
};

export default EditableMealTitle;
